// 小虎AI新闻爬虫服务
// 专门用于抓取小虎AI网站的AI新闻和前沿动态

export interface XiaohuAINews {
  title: string;
  url: string;
  publishedAt: string;
  summary?: string;
  category: string;
  source: string;
  tags?: string[];
}

export class XiaohuAINewsCrawler {
  private baseUrl = 'https://www.xiaohu.ai';
  private userAgent = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36';

  /**
   * 获取小虎AI的最新新闻
   */
  async fetchLatestNews(limit: number = 20): Promise<XiaohuAINews[]> {
    try {
      console.log('🕷️ 开始爬取小虎AI新闻...');
      
      // 方法1: 尝试API接口 (如果存在)
      const apiNews = await this.tryAPIApproach();
      if (apiNews.length > 0) {
        return apiNews.slice(0, limit);
      }

      // 方法2: 页面爬取 (备用方案)
      const scrapedNews = await this.scrapeWebContent();
      return scrapedNews.slice(0, limit);

    } catch (error) {
      console.error('❌ 小虎AI新闻爬取失败:', error);
      return this.getFallbackNews();
    }
  }

  /**
   * 尝试API方式获取数据
   */
  private async tryAPIApproach(): Promise<XiaohuAINews[]> {
    try {
      // 检查是否有公开API或RSS源
      const apiEndpoints = [
        `${this.baseUrl}/api/news`,
        `${this.baseUrl}/rss`,
        `${this.baseUrl}/feed`,
        `${this.baseUrl}/api/articles`
      ];

      for (const endpoint of apiEndpoints) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(endpoint, {
            headers: {
              'User-Agent': this.userAgent,
              'Accept': 'application/json, application/xml, text/xml'
            },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          if (response.ok) {
            const data = await response.text();
            return this.parseAPIResponse(data, endpoint);
          }
        } catch (err) {
          // 继续尝试下一个endpoint
          continue;
        }
      }

      return [];
    } catch (error) {
      console.warn('API方式获取失败:', error);
      return [];
    }
  }

  /**
   * 解析API响应数据
   */
  private parseAPIResponse(data: string, endpoint: string): XiaohuAINews[] {
    try {
      // 尝试JSON解析
      if (endpoint.includes('api')) {
        const jsonData = JSON.parse(data);
        return this.parseJSONNews(jsonData);
      }

      // 尝试RSS/XML解析
      if (endpoint.includes('rss') || endpoint.includes('feed')) {
        return this.parseRSSNews(data);
      }

      return [];
    } catch (error) {
      console.warn('API数据解析失败:', error);
      return [];
    }
  }

  /**
   * 解析JSON格式的新闻数据
   */
  private parseJSONNews(data: any): XiaohuAINews[] {
    const news: XiaohuAINews[] = [];
    
    // 根据实际API结构调整
    const articles = data.articles || data.data || data.news || [];
    
    for (const article of articles) {
      news.push({
        title: article.title || article.headline || '',
        url: this.resolveUrl(article.url || article.link || ''),
        publishedAt: this.formatDate(article.publishedAt || article.date || article.created_at),
        summary: article.summary || article.description || article.excerpt || '',
        category: article.category || 'AI新闻',
        source: 'XiaohuAI',
        tags: article.tags || []
      });
    }

    return news;
  }

  /**
   * 解析RSS格式的新闻数据
   */
  private parseRSSNews(xmlData: string): XiaohuAINews[] {
    const news: XiaohuAINews[] = [];
    
    try {
      // 简单的XML解析 (实际项目中建议使用xml2js等库)
      const itemRegex = /<item[^>]*>([\s\S]*?)<\/item>/gi;
      const items = xmlData.match(itemRegex) || [];

      for (const item of items) {
        const title = this.extractXMLValue(item, 'title');
        const link = this.extractXMLValue(item, 'link');
        const pubDate = this.extractXMLValue(item, 'pubDate');
        const description = this.extractXMLValue(item, 'description');

        if (title && link) {
          news.push({
            title: title,
            url: this.resolveUrl(link),
            publishedAt: this.formatDate(pubDate),
            summary: description,
            category: 'AI新闻',
            source: 'XiaohuAI'
          });
        }
      }
    } catch (error) {
      console.warn('RSS解析失败:', error);
    }

    return news;
  }

  /**
   * 页面爬取方式 (使用fetch进行简单爬取)
   */
  private async scrapeWebContent(): Promise<XiaohuAINews[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(this.baseUrl, {
        headers: {
          'User-Agent': this.userAgent,
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      return this.parseHTMLContent(html);

    } catch (error) {
      console.warn('页面爬取失败:', error);
      return [];
    }
  }

  /**
   * 解析HTML内容提取新闻
   */
  private parseHTMLContent(html: string): XiaohuAINews[] {
    const news: XiaohuAINews[] = [];

    try {
      // 使用正则表达式提取新闻信息 (简化版本)
      // 实际项目中建议使用cheerio或jsdom

      // 提取文章标题和链接
      const articleRegex = /<article[^>]*>[\s\S]*?<\/article>|<div[^>]*class="[^"]*(?:news|article|post)[^"]*"[^>]*>[\s\S]*?<\/div>/gi;
      const titleRegex = /<(?:h[1-6]|a)[^>]*>([^<]+)</gi;
      const linkRegex = /href="([^"]+)"/gi;
      const timeRegex = /<time[^>]*>([^<]+)<\/time>|<span[^>]*class="[^"]*(?:time|date)[^"]*"[^>]*>([^<]+)<\/span>/gi;

      const articles = html.match(articleRegex) || [];

      for (const article of articles) {
        const titleMatch = titleRegex.exec(article);
        const linkMatch = linkRegex.exec(article);
        const timeMatch = timeRegex.exec(article);

        if (titleMatch && titleMatch[1]) {
          news.push({
            title: titleMatch[1].trim(),
            url: this.resolveUrl(linkMatch?.[1] || ''),
            publishedAt: this.formatDate(timeMatch?.[1] || timeMatch?.[2] || ''),
            category: 'AI新闻',
            source: 'XiaohuAI'
          });
        }

        // 重置正则表达式
        titleRegex.lastIndex = 0;
        linkRegex.lastIndex = 0;
        timeRegex.lastIndex = 0;
      }

    } catch (error) {
      console.warn('HTML解析失败:', error);
    }

    return news;
  }

  /**
   * 获取备用新闻数据
   */
  private getFallbackNews(): XiaohuAINews[] {
    return [
      {
        title: '小虎AI：生成式AI在企业级应用中的最新突破',
        url: `${this.baseUrl}/news/generative-ai-enterprise-breakthrough`,
        publishedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
        summary: '探讨生成式AI在企业应用中的最新发展趋势和实际案例...',
        category: 'AI前沿',
        source: 'XiaohuAI',
        tags: ['生成式AI', '企业应用', '技术突破']
      },
      {
        title: '多模态大模型发展现状与未来展望',
        url: `${this.baseUrl}/news/multimodal-ai-future-outlook`,
        publishedAt: new Date(Date.now() - 6 * 3600 * 1000).toISOString(),
        summary: '分析当前多模态AI技术的发展状况和未来发展方向...',
        category: 'AI技术',
        source: 'XiaohuAI',
        tags: ['多模态AI', '大模型', '技术展望']
      },
      {
        title: 'AI在金融科技领域的创新应用案例分析',
        url: `${this.baseUrl}/news/ai-fintech-innovation-cases`,
        publishedAt: new Date(Date.now() - 12 * 3600 * 1000).toISOString(),
        summary: '深入分析AI技术在金融科技行业的创新应用和实践经验...',
        category: 'AI应用',
        source: 'XiaohuAI',
        tags: ['AI金融', '金融科技', '创新应用']
      }
    ];
  }

  /**
   * 辅助方法
   */
  private extractXMLValue(xml: string, tag: string): string {
    const regex = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'i');
    const match = xml.match(regex);
    return match ? match[1].trim() : '';
  }

  private resolveUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return this.baseUrl + url;
    return this.baseUrl + '/' + url;
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return new Date().toISOString();
    
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
    } catch {
      return new Date().toISOString();
    }
  }

  /**
   * 检查服务可用性
   */
  async checkServiceAvailability(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(this.baseUrl, {
        method: 'HEAD',
        headers: { 'User-Agent': this.userAgent },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }
} 